/**
 * Shared spool tracker UI palette — matches production progress cards
 * (complete teal, in-progress orange, soft mint/peach surfaces).
 */
export const SP_HEX = {
  complete: '#1E6353',
  orange: '#D9531E',
  track: '#E2E8F0',
  labelSlate: '#64748B',
  mintBg: '#ECF4F2',
  mintBorder: '#C8D4D0',
  stepHeaderBg: '#FAF0EE',
  stepHeaderBorder: '#EDE4E0',
} as const;

/** Tailwind arbitrary-value class strings (safe for JIT — static literals). */
export const spoolTw = {
  progCompleteBar: 'bg-[#1E6353]',
  progOrangeBar: 'bg-[#D9531E]',
  progCompleteText: 'text-[#1E6353]',
  progOrangeText: 'text-[#D9531E]',
  /** Completed steps in the main stepper (mint pill + teal check). */
  stepDonePill: 'bg-[#ECF4F2] text-[#1E6353] hover:bg-[#E0EDE9]',
  stepDoneCheck: 'bg-[#1E6353] text-white',
  stepDoneChevron: 'text-[#1E6353]/65',
  /** Current (active) step pill — same orange as in-progress bars. (Shadow kept small to avoid visual overlap on the horizontal step strip.) */
  stepActivePill: 'bg-[#D9531E] text-white shadow-sm ring-1 ring-white/15 hover:bg-[#D9531E]/90',
  stepActiveNum: 'bg-white text-[#1E6353]',
  roleQcPill: 'bg-[#E8F2EF] text-[#1E6353]',
  /** Spool card status chip */
  statusFatComplete: 'bg-[#E8F2EF] text-[#1E6353] border-[#C8D4D0]',
  statusFatInProgress: 'bg-[#FDF3EF] text-[#D9531E] border-[#F0D4C8]',
  /** “Step N — …” bar under stepper */
  stepHeaderBar: 'bg-[#FAF0EE] border-y border-[#EDE4E0]',
  /** Tri-state row — pass (white card like VT checklist mock). */
  triPassRow: 'bg-white border-gray-200 hover:bg-gray-50',
  /** Square checkbox / pass icon — forest teal fill, white mark. */
  triPassIcon: 'border-[#1E6353] bg-[#1E6353] text-white',
  /** Read-only checklist row — white card, light border. */
  checkRowCard: 'flex items-center gap-3 rounded-md border border-gray-200 bg-white py-2.5 px-3 text-sm text-slate-900',
  /**
   * Radix Checkbox overrides — production green when checked (replaces default red in ui/checkbox).
   * Use on spool tracker checklist rows only.
   */
  checkboxProduction:
    'h-5 w-5 shrink-0 rounded border border-gray-300 shadow-none transition-colors [&_svg]:h-3.5 [&_svg]:w-3.5 ' +
    'data-[state=checked]:bg-[#1E6353] data-[state=checked]:border-[#1E6353] data-[state=checked]:text-white ' +
    'hover:border-gray-400 data-[state=checked]:hover:bg-[#175544] data-[state=checked]:hover:border-[#175544] ' +
    'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#1E6353]/35 focus-visible:ring-offset-0',
  /** FAT left list */
  fatMiniBar: 'bg-[#1E6353]',
  fatDoneIcon: 'bg-[#1E6353] text-white',
  fatCurrentIcon: 'bg-[#D9531E] text-white',
  fatDoneBadge: 'border-transparent bg-[#E8F2EF] text-[#1E6353]',
  fatActiveRow: 'border-[#D9531E]/50 bg-[#FDF3EF]',
  fatHeaderCalm: 'bg-[#ECF4F2] border-b border-[#D5E4DE]',
  fatHeaderWitness: 'bg-[#FDF3EF] border-b border-[#F0D4C8]',
  fatQcBadge: 'border-transparent bg-[#E8F2EF] text-[#1E6353]',
  fatBuyerBadge: 'border-transparent bg-orange-100 text-orange-900',
  fatPrimaryBtn: 'bg-[#1E6353] hover:bg-[#175544] text-white',
  /** Spool cards — hover accent */
  cardHoverBorder: 'hover:border-[#1E6353]/35',
  cardTitleHover: 'group-hover:text-[#1E6353]',
  cardHintText: 'text-[#1E6353]',
  /** Weld joint status — pass-like */
  weldPassBadge: 'bg-[#E8F2EF] text-[#1E6353] border-[#C8D4D0]',
  /** StepInfoBanner success — aligned with pass / done mint */
  stepBannerSuccess: 'rounded-lg border border-[#C8D4D0] bg-[#ECF4F2] border-l-4 border-l-[#1E6353]',
  stepBannerSuccessTitle: 'text-[#1E6353]',
  stepBannerSuccessBody: 'text-gray-800',
} as const;
